<script setup lang="ts">
const emit = defineEmits<{
  (e: "open"): void;
}>();

const { coupleName, guestText, openInvitation } = useOpeningCard();

async function onOpen() {
  if (await openInvitation()) {
    emit("open");
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-60 flex h-safe-min-screen items-center justify-center overflow-hidden bg-primary-950/20 backdrop-blur-[6px]"
    role="dialog"
    aria-modal="true"
    aria-labelledby="cover-dialog-title"
    aria-label="Kartu Undangan Walimatul ‘Ursy"
  >
    <div class="flex size-full flex-1 overflow-y-auto p-4 xs:p-6 md:p-8 lg:p-10">
      <div
        class="opening-card-shell relative isolate m-auto flex min-h-[590px] w-full max-w-[460px] flex-col overflow-hidden rounded-t-[220px] rounded-b-[24px] border border-white/80 bg-primary-50 xs:min-h-[640px] md:min-h-[680px]"
      >
        <!-- Watercolor Paper -->
        <NuxtImg
          src="/hero-bg-watercolor-desktop.webp"
          alt=""
          aria-hidden="true"
          class="absolute inset-0 -z-3 size-full object-fill opacity-85"
          loading="eager"
          decoding="async"
          fetchpriority="high"
          :preload="{
            fetchPriority: 'high',
          }"
        />

        <!-- Independently Positioned Line-Art Flowers -->
        <NuxtImg
          src="/hero-flowers-right.webp"
          alt=""
          aria-hidden="true"
          class="-z-2 opacity-40 absolute -right-10 top-0 w-70 xs:-right-12 xs:top-0 xs:w-80 md:-right-14 md:top-0"
          loading="eager"
          decoding="async"
        />

        <NuxtImg
          src="/hero-flowers-left.webp"
          alt=""
          aria-hidden="true"
          class="opacity-30 -z-2 absolute -bottom-18 -left-18 w-60 xs:-bottom-20 xs:-left-20 xs:w-72 md:-bottom-24 md:-left-24 md:w-80"
          loading="eager"
          decoding="async"
        />

        <div class="absolute inset-x-[14%] top-10 -z-1 h-48 rounded-full bg-white/72 blur-3xl" />
        <div class="absolute inset-x-[18%] bottom-10 -z-1 h-40 rounded-full bg-primary-50/55 blur-3xl" />

        <div class="opening-card-content flex flex-1 flex-col items-center justify-center px-7 py-12 text-center text-primary-900 xs:px-9 xs:py-14 sm:px-11 md:py-16">
          <div class="mx-auto flex w-full max-w-sm flex-col items-center">
            <header class="flex flex-col items-center">
              <p class="font-cursive text-xl font-bold normal-case text-primary-700 xs:text-2xl">
                Bismillahirrahmanirrahim
              </p>

              <p class="mt-5 text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-700/80 xs:text-xs">
                Undangan Walimatul ‘Ursy
              </p>

              <h1
                id="cover-dialog-title"
                class="mt-4 whitespace-nowrap font-cursive text-5xl font-bold normal-case leading-none text-primary-900 drop-shadow-[0_1px_0_rgba(255,255,255,0.9)] xs:text-6xl sm:text-7xl"
              >
                {{ coupleName }}
              </h1>
            </header>

            <div class="mt-12 flex w-full flex-col items-center xs:mt-14">
              <p class="font-cursive text-xl font-bold normal-case text-primary-700 xs:text-2xl">
                Kepada Yth.
              </p>

              <div class="mt-2 flex min-h-14 items-center justify-center">
                <h2 class="max-w-xs text-pretty line-clamp-2 font-serif text-2xl font-medium uppercase leading-tight tracking-[0.04em] text-primary-900 xs:text-[28px]">
                  {{ guestText }}
                </h2>
              </div>
            </div>

            <footer class="mt-10 flex max-w-xs flex-col items-center gap-8 xs:mt-12">
              <p class="text-pretty text-xs font-medium leading-6 tracking-[0.02em] text-primary-800 xs:text-sm xs:leading-7">
                Kami mengundang Anda untuk bergabung bersama kami dalam momen bahagia ini.
              </p>

              <UButton
                label="Buka Undangan"
                class="opening-card-button mx-auto w-fit rounded-full border border-primary-500/25 px-7 shadow-[0_12px_28px_-14px_rgba(40,41,62,0.5)] transition duration-300 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-50"
                size="xl"
                block
                @click="onOpen"
              />
            </footer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.opening-card-shell {
  background:
    radial-gradient(circle at 78% 8%, rgba(214, 221, 239, 0.44), transparent 30%),
    linear-gradient(155deg, #fff 0%, #f8f9fc 50%, #f3f6fa 100%);
  box-shadow:
    0 34px 80px -30px rgba(40, 41, 62, 0.42),
    0 14px 32px -20px rgba(40, 41, 62, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
}

.opening-card-button {
  color: #fff;
  background: #6162a1;
}

.opening-card-button:hover {
  color: #fff;
  background: #505183;
  box-shadow: 0 16px 32px -14px rgba(40, 41, 62, 0.56);
}

.opening-card-button :deep(span) {
  letter-spacing: 0.04em;
}

@media (max-height: 700px) {
  .opening-card-shell {
    min-height: 560px;
  }

  .opening-card-content {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }
}
</style>
